import React, { PropsWithChildren } from 'react';

export interface AIPanelConfig {
  title: string;
}

export interface AIPanelContextValue {
  isOpen: boolean;
  title: string | null;
  portalContainer: HTMLDivElement | null;
  setPortalContainer: (el: HTMLDivElement | null) => void;
  open: (config: AIPanelConfig) => void;
  close: () => void;
  toggle: (config: AIPanelConfig) => void;
  hasContent: boolean;
}

export const AIPanelContextV3 = React.createContext<AIPanelContextValue | null>(null);

export const AIPanelProviderV3: React.FC<PropsWithChildren> = ({ children }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [title, setTitle] = React.useState<string | null>(null);
  const [hasContent, setHasContent] = React.useState(false);
  const [portalContainer, setPortalContainer] = React.useState<HTMLDivElement | null>(null);

  const open = React.useCallback((config: AIPanelConfig) => {
    setTitle(config.title);
    setIsOpen(true);
  }, []);

  const close = React.useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggle = React.useCallback((config: AIPanelConfig) => {
    setIsOpen(prev => {
      if (!prev) {
        setTitle(config.title);
      }
      return !prev;
    });
  }, []);

  // Watch for content changes in the portal container via MutationObserver.
  // When the caller unmounts (e.g. on navigation), the portal content disappears,
  // and we auto-close the panel and clear the title.
  React.useEffect(() => {
    if (!portalContainer) {
      return undefined;
    }

    const updateHasContent = () => {
      const contentPresent = portalContainer.childNodes.length > 0;
      setHasContent(contentPresent);
      if (!contentPresent) {
        setIsOpen(false);
        setTitle(null);
      }
    };

    // Check initial state
    updateHasContent();

    const observer = new MutationObserver(updateHasContent);
    observer.observe(portalContainer, { childList: true });

    return () => {
      observer.disconnect();
    };
  }, [portalContainer]);

  const contextValue = React.useMemo<AIPanelContextValue>(
    () => ({
      isOpen,
      title,
      portalContainer,
      setPortalContainer,
      open,
      close,
      toggle,
      hasContent,
    }),
    [isOpen, title, portalContainer, open, close, toggle, hasContent],
  );

  return <AIPanelContextV3.Provider value={contextValue}>{children}</AIPanelContextV3.Provider>;
};
